import React, { useEffect, useState, useContext } from "react";
import ReactDatePicker from "react-datepicker";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getDomainConfig } from "../../config/config";
import { getGuestListDetails } from "../../utils/cart.js";
import StandardFormItem from "../UI/StandardFormItem";
import StandardInputWrapper from "../UI/StandardInputWrapper";
import StandardLabel from "../UI/StandardLabel";
import StandardValidation from "../UI/StandardValidation";
import { googleDataLayer } from "../Analytics/Universal/PageView/googleDataLayer";
import { ABContext } from "../../contexts/ab-context";
import { getLanguageCode } from "../../utils/getURLParams.js";
import { PhoneNumberFormatter } from "../../utils/phoneNumberFormatter.js";
import { Logs } from "../../utils/logs.js";

function calculateDOBRange(date, yearsToSubtract, yearsToAdd = 0) {
  const dateCopy = new Date(date);
  if (yearsToAdd > 0) {
    dateCopy.setFullYear(dateCopy.getFullYear() - (+yearsToSubtract + 1));
    dateCopy.setFullYear(dateCopy.getFullYear() + yearsToAdd);
  } else {
    dateCopy.setFullYear(dateCopy.getFullYear() - (+yearsToSubtract + 1));
    dateCopy.setDate(dateCopy.getDate() + 1);
  }

  return dateCopy;
}

export const PhoneField = (props) => {
  const { t } = useTranslation('common');

  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess]= useState(false);
  const [forceTouch, setForceTouch] = useState(false);

  const phoneNumberFormatter = new PhoneNumberFormatter();
  const countries = phoneNumberFormatter.getCountryCodes();

  const [phoneFieldMask, setPhoneFieldMask] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [phoneNumberMinLength, setPhoneNumberMinLength] = useState(0);

  const fieldOptions = countries.map((opt) => {
    return (
      <option key={opt} value={opt}>
        {phoneNumberFormatter.formatCountryCode(opt)}
      </option>
    );
  });

  // set country code by region
  useEffect(() => {
    const countryGeo = getLanguageCode('Atd');
    let defaultCountry = "";

    switch(countryGeo) {
      case "us":
        defaultCountry = "US";
        break;
      case "en-nl":
        defaultCountry = "NL";
        break;
      case "au":
        defaultCountry = "AU";
        break;
      case "en-ie":
        defaultCountry = "IE";
        break;
      case "de":
        defaultCountry = "DE";
        break;
      default:
        defaultCountry = "GB";
    }
    setCountryCode(defaultCountry);
  }, [])

  const onSelectChange = (e) => {
    const country = e.target.value;
    setCountryCode(country);
    props.setValue('phone-field-masked', '');
    setShowSuccess(false);
    setShowError(false);
    setForceTouch(false);
  }

  useEffect(() => {
    if (countryCode !== '') {
      setPhoneFieldMask(phoneNumberFormatter.getPhoneFieldMask(countryCode));
      props.setValue('phone-field-country-code', countryCode, {shouldDirty:true,shouldTouch:true})
      setPhoneNumberMinLength(phoneNumberFormatter.getPhoneNumberMinLength(countryCode));
    }
  },[countryCode])


  return (
    <>
      <Select
        register={props.register}
        errors={props.errors}
        getValues={props.getValues}
        setValue={props.setValue}
        onChange={(e) => onSelectChange(e)}
        required={true}
        name={'phone-field-country-code'}
        options={fieldOptions}
        errorText={'error'}
        initialOption={countryCode}
        maskingClass={props.maskingClass}
        control={props.control}
        className={'phone-field-country-code'}
        dataLayerInput={() => googleDataLayer.createUserClickInputEvent('phone-field-country-code')}
      />
      <Textfield
        type={'tel'}
        register={props.register}
        errors={props.errors}
        setError={props.setError}
        getValues={props.getValues}
        name={'phone-field-masked'}
        required={true}
        errorText={t('please-enter-a-phone-number')}
        pattern={/^\d+$/}
        placeholder={phoneFieldMask}
        minLength={phoneNumberMinLength}
        setValue={props.setValue}
        maskingClass='gb-mask'
        autocomplete={'tel'}
        dataLayerInput={() => googleDataLayer.createUserClickInputEvent('phone-field-masked')}
        showSuccess={showSuccess}
        showError={showError}
        forceTouch={forceTouch}
        validate={(value) => {
          let valid = phoneNumberFormatter.validateNumber(value, props.getValues('phone-field-country-code'));
          setForceTouch(valid);
          return valid;
        }}
      />
    </>
  )
}

export const Textfield = (props) => {
  const config = getDomainConfig();

  const { t } = useTranslation('common');
  const [keyPressed,setKeyPressed] = useState(false);

  /* LOADING THE BRANDED SCSS */
  const [styles, setStyles] = useState(false);
  useEffect(() => {
    const getStyles = async (theFileName, theFileEnd) => {
      if (config.brand) {
        let moduleStyles = false;
        try {
          moduleStyles = await import(
            "./" + theFileName + config.brand + theFileEnd
          );
          setStyles(moduleStyles);
        } catch (e) {
          console.error(
            `Styles for ${theFileName} could not be loaded, ${e.message}`
          );
        }
      }
    };

    // Specify file name and end of file name here.
    getStyles("Input", ".module.scss");
  }, [config.brand]);

  /* END LOADING THE BRANDED SCSS */

  let defaultClass = styles.textInput; // The generic class (in css modules format)
  let classes = defaultClass; // The classes list to show
  let showError = props.showError || false;
  let showSuccess = props.showSuccess || false;

  if (props.errors[props.name]) {
    // If validation triggered, add another modules class
    classes = defaultClass + " " + styles.error;
    showError = true;
    showSuccess = false;

  } else if (props.touched || props.forceTouch) {

    // Input has been touched and is valid.
    classes = defaultClass + " " + styles.success;
    showError = false;
    showSuccess = true;
  }

  let minLength = 0;
  if (props.minLength) {
    minLength = props.minLength;
  }

  let pattern = {};
  if (props.pattern) {
    pattern = { value: props.pattern, message: props.errorText };
  }

  if (props.className) {
    classes = classes + " " + props.className;
  }

  if (props.maskingClass) {
    classes = classes + " " + props.maskingClass
  }

  if (showError) {
   Logs.AddEvent(`Validation failed for ${props.name} `, true)
  }

  const placeholderText = (placeholder, label) => {
    if (!placeholder) {
      return label;
    }
    if (
      placeholder === "Full Passenger Name" ||
      placeholder === "Full Passenger name" ||
      placeholder === "Lead passenger name"
    ) {
      return t("full-name-and-surname");
    }

    if (label) {
      if (
        label.includes("Lead passenger name") ||
        label.includes("Full Passenger name") ||
        label.includes("Full Passenger Name") ||
        label.includes("Passenger name")
      ) {
        return t("full-name-and-surname");
      }
    }
    return placeholder;
  };

  const populateInputValues = (event, props) => {
    const attributeTypeAndIndex = props.attributeTypeAndIndex;
    const fieldValue = event.target.value;

    props.setValue(props.name,fieldValue, { shouldDirty: true ,shouldTouch: true , shouldValidate: true })

    // disable the copy of names for now until we do it properly
   /* if (attributeTypeAndIndex !== undefined) {
      const inputVar = attributeTypeAndIndex.split("_");
      if (inputVar[0] === "guest") {
        const [, ...attributeTypeAndIndex] = inputVar;
        const inputFields = document.querySelectorAll(
          `[attributeTypeAndIndex=${attributeTypeAndIndex.join("_")}]`
        );
        inputFields.forEach((inputField) => {
          // get the key.
          const attributeName = inputField.getAttribute("name");
          props.setValue(attributeName, event.target.value, {shouldDirty:true,shouldTouch:true});
          inputField.setAttribute("readonly", "true");
        });
      }
    }*/

    if (props.onChange) {
      props.onChange(event);
    }

    if (props.setGuestInfo) {
      props.setGuestInfo({
        firstName: '',
        lastName: '',
        isMainGuestChecked: false
      })
    }

  };

  const logAutocomplete = () => {
    if (keyPressed === 'Unidentified') {
      return googleDataLayer.createAutocompleteEvent(props.name,props.attTitle,props.product,props.fieldTitle)
    }
  }

  const trimAndRevalidateField = (e) => {
    const fieldValue = e.target.value;
    const fieldName = e.target.name;

    props.setValue(fieldName, fieldValue.trimEnd().trimStart(), {shouldTouch:true, shouldDirty: true, shouldValidate: true});
  }

  return (
    <StandardFormItem className="textItem">
      {!props.hideLabel && (
      <StandardLabel htmlFor={props.name} required={props.required}>
        {props.label}
      </StandardLabel>
      )}
      <StandardInputWrapper className="textWrapper">
          <input
            size={props.inputSize}
            minLength={props.minLength}
            maxLength={props.maxLength}
            pattern={props.pattern}
            type={props.type || 'text'}
            className={classes}
            name={props.name}
            id={props.id}
            attributeTypeAndIndex={props.attributeTypeAndIndex}
            placeholder={placeholderText(props.placeholder, props.label)}
            required=""
            autoComplete={props.autocomplete}
            {...props.register(props.name, {
              required: props.required,
              minLength: minLength,
              pattern: pattern,
              validate: props.validate || null,
              onChange: (e) => {
                return e.target.value
              },
              onBlur: (e) => {
                if (props.onBlur) {
                  props.onBlur(e)
                }
                trimAndRevalidateField(e)
              }
            })}
            onChange={(e) => {
              populateInputValues(e, props)
            }}
            onClick={props.dataLayerInput}
            onKeyDownCapture={(e) => setKeyPressed(e.key)}
            onInput={() => logAutocomplete()}
            onKeyUp={(event) =>{
              if(props.onKeyUp) {
                props.onKeyUp(event);
              }
            }}
          />
        {showSuccess && (
          <StandardValidation
            valType="success"
            icon="true"
            className="input"
          ></StandardValidation>
        )}
        {showError && (
          <StandardValidation className="input" valType="error" icon="true">
            {props.errorText}
          </StandardValidation>
        )}
      </StandardInputWrapper>
    </StandardFormItem>
  );
};

export const Select = (props) => {
  const config = getDomainConfig();
  const { t } = useTranslation('common');

  /* LOADING THE BRANDED SCSS */
  const [styles, setStyles] = useState(false);

  useEffect(() => {
    const getStyles = async (theFileName, theFileEnd) => {
      if (config.brand) {
        let moduleStyles = false;
        try {
          moduleStyles = await import(
            "./" + theFileName + config.brand + theFileEnd
          );
          setStyles(moduleStyles);
        } catch (e) {
          console.error(
            `Styles for ${theFileName} could not be loaded, ${e.message}`
          );
        }
      }
    };

    // Specify file name and end of file name here.
    getStyles("Input", ".module.scss");
  }, [config.brand]);

  /* END LOADING THE BRANDED SCSS */
  let defaultClass = styles.selectInput; // The generic class (in css modules format)
  let classes = defaultClass; // The classes list to show
  let showError = false;
  let showSuccess = false;

  const onValueChange = (e) => {
    props.dataLayerInput(e)
    if (props.name === 'billing-address-region') {
      props.setValue(props.name,e.target.value,{shouldDirty:true,shouldTouch:true, shouldValidate:true})
    } else {
      props.setValue(props.name,e.target.value,{shouldDirty:true,shouldTouch:true})
    }
    showError = false;
  }
  const attributeState = props.control.getFieldState(props.name)
  const fieldValue = props.getValues(props.name);

  // Show Error message if we need to.
  if (attributeState.isDirty && attributeState.isTouched && fieldValue !== '') {
    showSuccess = true;
    classes = defaultClass + " " + styles.success;
  }
  if ((attributeState.invalid && attributeState.error !== undefined) || (props.touched && (fieldValue === '' || fieldValue === t('please-select')))) {
    showError = true;
    classes = defaultClass + " " + styles.error;
  }

  if (props.label ===  t("country")) {
    props.setValue(props.name, props.initialOption, {shouldDirty:true,shouldTouch:true});
    classes = defaultClass + " " + styles.success;
    showError = false;
    showSuccess = true;
  }

  if (props.maskingClass) {
    classes = classes + " " + props.maskingClass
  }

  if (showError) {
   Logs.AddEvent(`Validation failed for ${props.name} `, true)
  }

  return (
    <StandardFormItem className="selectItem">
      <StandardLabel htmlFor={props.name} required={props.required}>
        {props.label}
      </StandardLabel>
      <StandardInputWrapper className="selectWrapper">
        <select
          className={classes}
          name={props.name}
          id={props.name}
          required=""
          {...props.register(props.name, {
            required: props.required
          })}
          onChange={props.onChange}
          onInput={onValueChange}
          autoComplete={props.autocomplete}
        >
          {props.showInitialOption &&
            <option value="">{props.initialOption}</option>
          }
          {props.options}
        </select>

        {showSuccess && (
          <StandardValidation
            valType="success"
            icon="true"
            className="select"
          ></StandardValidation>
        )}
        {showError && (
          <StandardValidation className="select" valType="error" icon="true">
            {props.errorText}
          </StandardValidation>
        )}
      </StandardInputWrapper>
    </StandardFormItem>
  );
};

export const  Checkbox = (props) => {
  const config = getDomainConfig();

  /* LOADING THE BRANDED SCSS */
  const [styles, setStyles] = useState(false);

  useEffect(() => {
    const getStyles = async (theFileName, theFileEnd) => {
      if (config.brand) {
        let moduleStyles = false;
        try {
          moduleStyles = await import(
            "./" + theFileName + config.brand + theFileEnd
          );
          setStyles(moduleStyles);
        } catch (e) {
          console.error(
            `Styles for ${theFileName} could not be loaded, ${e.message}`
          );
        }
      }
    };

    // Specify file name and end of file name here.
    getStyles("Input", ".module.scss");
  }, [config.brand]);

  /* END LOADING THE BRANDED SCSS */

  const [isChecked, setIsChecked] = useState(props.checked || false);

  let defaultClass = styles.checkboxInput; // The generic class (in css modules format)
  let classes = defaultClass; // The classes list to show
  let showError = false;
  let showSuccess = false;
  if (props.errors[props.name]) {
    // If validation triggered, add another modules class
    classes = defaultClass + " " + styles.error;
    showError = true;
    showSuccess = false;
  } else if (props.touched && isChecked) {
    // Input has been touched (and is checked) and is valid.
    classes = defaultClass + " " + styles.success;
    showError = false;
    showSuccess = true;
  }

  let defaultLabelClass = "checkboxLabel"; // The generic class (in css modules format)
  let labelClasses = defaultLabelClass; // The classes list to show
  if (isChecked) {
    // If validation triggered, add another modules class
    labelClasses = defaultLabelClass + " checked";
  }

  // Check when someone clicks the checkbox
  const handleChange = (event) => {
    setIsChecked(event.target.checked);
    if (props.onChange) {
      props.onChange(event);
    }
  };

  if (showError) {
   Logs.AddEvent(`Validation failed for ${props.name} `, true)
  }

  return (
    <StandardFormItem className="checkboxItem">
      <StandardInputWrapper className="checkboxWrapper">
        <StandardLabel
          className={labelClasses}
          htmlFor={props.name}
          required={props.required}
          name={props.name}
        >
          <input
            className={classes}
            type="checkbox"
            name={props.name}
            id={props.name}
            {...props.register(props.name, {
              required: props.required})}
            defaultChecked={props.checked}
            onChange={handleChange}
            onClick={props.onClick}
            onBlur={props.onBlur && props.onBlur}
          />
          <span></span>
          {props.label}
        </StandardLabel>
        {showSuccess && (
          <StandardValidation
            valType="success"
            className="checkBox"
          ></StandardValidation>
        )}
        {showError && (
          <StandardValidation className="checkBox error" valType="error" id="termsError">
            {props.errorText}
          </StandardValidation>
        )}
      </StandardInputWrapper>
    </StandardFormItem>
  );
};

export const DeliveryRadios = (props) => {
  const config = getDomainConfig();

  /* LOADING THE BRANDED SCSS */
  const [styles, setStyles] = useState(false);

  useEffect(() => {
    const getStyles = async (theFileName, theFileEnd) => {
      if (config.brand) {
        let moduleStyles = false;
        try {
          moduleStyles = await import(
            "./" + theFileName + config.brand + theFileEnd
          );
          setStyles(moduleStyles);
        } catch (e) {
          console.error(
            `Styles for ${theFileName} could not be loaded, ${e.message}`
          );
        }
      }
    };

    // Specify file name and end of file name here.
    getStyles("Input", ".module.scss");
  }, [config.brand]);

  /* END LOADING THE BRANDED SCSS */

  const deliveryOptions = props.options.map((option) => {
    let checked = false;

    // Select the default option.
    if (option.id === "4" && !props.selected) {
      checked = true;
    } else if (props.selected === option.id) {
      checked = true;
    }

    let defaultClass = styles.radioInput; // The generic class (in css modules format)
    let classes = defaultClass; // The classes list to show

    return (
      <StandardFormItem className="radioItem" key={"shipping-" + option.id}>
        <StandardLabel
          className="radioLabel"
          htmlFor={"shipping-" + option.id}
          required={props.required}
          selected={checked}
        >
          <span className={styles.label}> {option.description} </span>
          <span className={styles.amount}> £{option.price} </span>
        </StandardLabel>
        <StandardInputWrapper className="radioWrapper">
          <input
            type="radio"
            id={"shipping-" + option.id}
            name="quote-option"
            className={classes}
            defaultChecked={checked}
            value={option.id}
            onChange={props.onChange}
          />
          <span></span>
        </StandardInputWrapper>
      </StandardFormItem>
    );
  });

  return (
    <div className={styles.radioHolder} id={props.name}>
      {deliveryOptions}
    </div>
  );
};

export const GuestListAttribute = ({ props, att, index, ticket, attributeTypeAndIndex, maskingClass, dataLayerInput }) => {
  // Every Guest has a select and a text field.
  // Children and infants have a date of birth with restrictions.
  const guestListDetails = getGuestListDetails(ticket.rawAttributes);
  const config = getDomainConfig()

  const { t } = useTranslation('common');

  // CRO-1381-checkout-redesign
  const abContext = useContext(ABContext);
  const [isCro1381, setIsCro1381] = useState(false);

  useEffect(() => {
    if ((config.brand !== 'Sw') && abContext.cro1381 !== undefined) setIsCro1381(abContext.cro1381.variant);
  }, [abContext]);
  // END CRO-1381-checkout-redesign

  const titles = [t('Mr'),"Mrs",t('Ms'),"Miss"];
  const titlesOptions = titles.map((title) => {
    return (
      <option key={title} value={title}>
        {title}
      </option>
    );
  });

  let guestListFieldsGrouped = [];

  // Loop through the guest list details.
  Object.keys(guestListDetails).forEach((type) => {
    // Getting the ticket Date
    const checkoutDate = ticket.rawAttributes[42][0];
    let childrenAges = ticket.rawAttributes[24][0];

    if (childrenAges.includes(',') && config.brand === "Uo") {
      childrenAges = ticket.rawAttributes[24][0].split(',');
    }

    if (config.brand === "Atd") {
      childrenAges = ticket.rawAttributes[24][0].split(',');
    }

    const hotelCheckoutDate = new Date(checkoutDate.toLocaleString('en-GB'));

    // Calculating infants DOB
    const maxInfantAge = new Date(hotelCheckoutDate.setFullYear(hotelCheckoutDate.getFullYear() - 3));
    maxInfantAge.setDate(maxInfantAge.getDate() + 1);

    const noGuests = guestListDetails[type];

    for (let n = 0; n < noGuests; n += 1) {
      const guestListFields = [];
      guestListFields.push(
        <h2 className="guestnum">
          {type.charAt(0).toUpperCase() + type.slice(1)} {n + 1}
        </h2>
      );

      const guestTitle =
        att.key + "_guest_title_" + type + "_" + index + "_" + n;
      const guestName = att.key + "_guest_name_" + type + "_" + index + "_" + n;
      const guestDate = att.key + "_guest_date_" + type + "_" + index + "_" + n;
      const onValueChange = (e) => {
            props.setValue(guestTitle, e.target.value, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
          };
      // Add select.
      guestListFields.push(
        <Select
          showInitialOption={true}
          register={props.register}
          errors={props.errors}
          getValues={props.getValues}
          inputSize={32}
          required={true}
          name={guestTitle}
          options={titlesOptions}
          errorText={t('please-select-a-title')}
          initialOption={t('please-select')}
          label={t('title')}
          onChange={onValueChange}
          touched={props.touchedFields[guestTitle]}
          setValue={props.setValue}
          maskingClass={maskingClass}
          control={props.control}
          dataLayerInput={() => googleDataLayer.createUserClickInputEvent("guest_title")}
        />
      );

      // Add name.
      guestListFields.push(
        <Textfield
          className={"name"}
          placeholder={t('first-name')}
          label={t('first-name')}
          register={props.register}
          errors={props.errors}
          inputSize={32}
          attributeTypeAndIndex={"guest_" + type + "_" + n}
          required={att.required}
          name={guestName}
          type={att.type}
          errorText={t('please-enter-value-for-firstname')}
          touched={props.touchedFields[guestName]}
          setValue={props.setValue}
          maskingClass={maskingClass}
          dataLayerInput={dataLayerInput}
        />,
        <Textfield
          className={"name"}
          placeholder={t('last-name')}
          label={t('last-name')}
          register={props.register}
          errors={props.errors}
          inputSize={32}
          attributeTypeAndIndex={"guest_" + type + "_" + n + "_" + 1}
          required={att.required}
          name={guestName + '_' + (n + 1)}
          type={att.type}
          errorText={t('please-enter-value-for-lastname')}
          touched={props.touchedFields[guestName + '_' + (n + 1)]}
          setValue={props.setValue}
          maskingClass={maskingClass}
          dataLayerInput={dataLayerInput}
        />
      );

      if (type === "child") {
        // Calculating children DOB for date range to chose from
        const maxChildDOB = calculateDOBRange(checkoutDate,childrenAges[n]);
        const minChildDOB = calculateDOBRange(checkoutDate,childrenAges[n],1)

        // Switching off populating DOB fields, was causing an issue for Eurostar
        // const populateDateValues = (event, props, attributeTypeAndIndex) => {

        //       const inputFields = document.querySelectorAll(
        //         `[attributetypeandindex=${"date_" + attributeTypeAndIndex}]`
        //       );
        //       inputFields.forEach((inputField) => {
        //         // get the key.
        //         const attributeName = inputField.getAttribute("id");
        //         props.setValue(attributeName, event, {shouldDirty:true,shouldTouch:true});
        //       });
        // };

        guestListFields.push(
          <React.Fragment>
            <div className="date-item">
              <StandardLabel htmlFor={guestDate} required="true">{t('birth-date')}</StandardLabel>
              <Controller
                control={props.control}
                name={guestDate}
                rules={{required : `${t('please-enter-value-for')} ${t('birth-date')}`}}
                render={({ field: { onChange, onBlur, value, ref }, fieldState: { error, invalid }}) => {
                  const showSuccess = value && !invalid;

                  return (
                    <>
                      <ReactDatePicker // validation infants 2 y or under, child between 2-18
                        placeholderText={t('birth-date')}
                        ref={(e) => {e && ref(e.input)}}
                        dateFormat="dd MMM yyyy"
                        onChange={(e) => {
                          onChange(e);
                          googleDataLayer.createUserClickInputEvent('date_of_birth');
                        }}
                        onCalendarClose={() => props.trigger(guestDate)}
                        onBlur={onBlur}
                        selected={value}
                        showYearDropdown
                        minDate={maxChildDOB}
                        maxDate={minChildDOB}
                        className={"dob gb-mask"}
                        // customInput is used to apply custom attributes to the HTML element rendered by ReactDatePicker.
                        // In this scenario we are adding 'daterange' attribute for testing purposes as the ReactDatePicker cannot be rendered in Jest test.
                        // Also, we are checking if environment where code is running is Jest test or not to not generate this attribute in the other environment than testing.
                        customInput={
                          (process.env.JEST_WORKER_ID !== undefined) ? <input daterange={`${maxChildDOB} - ${minChildDOB}`} /> : <input inputmode="none" autocomplete="off" data-cy="dob"/>
                        }
                        id={guestDate}
                        {...(isCro1381 && {
                          popperPlacement: "bottom-start",
                          popperModifiers: [
                            {
                              name: "flip",
                              options: {
                                fallbackPlacements: ["bottom-start"],
                                allowedAutoPlacements: ["bottom-start"],
                              },
                            },
                          ],
                          showPopperArrow: false,
                        })}
                      />
                      {error && (
                        <StandardValidation
                        id="dob"
                        valType="error"
                        icon="true"
                        className={`input ${!isCro1381 ? 'dobValidation dobValidationSpacer' : ''}`}
                        >
                        {error.message}
                        </StandardValidation>
                      )}
                      {showSuccess && (
                        <StandardValidation
                        id="dob"
                        valType="success"
                        icon="true"
                        className={`input ${!isCro1381 ? 'dobValidation' : ''}`}
                        />
                      )}
                    </>
                  )
                }}
              />
            </div>
          </React.Fragment>
        );
      }

      if (type === "infant") {
        guestListFields.push(
          <React.Fragment>
            <div className="date-item">
              <StandardLabel htmlFor={guestDate} required="true">{t('birth-date')}</StandardLabel>
              <Controller
                control={props.control}
                name={guestDate}
                rules={{required : `${t('please-enter-value-for')} ${t('birth-date')}`}}
                render={({ field: { onChange, onBlur, value, ref }, fieldState: { error, invalid }}) => {
                  const showSuccess = value && !invalid;
                  return(
                    <>
                    <ReactDatePicker // validation infants 2 y or under, child between 2-18
                      placeholderText={t('birth-date')}
                      ref={(e) => {e && ref(e.input)}}
                      dateFormat="dd MMM yyyy"
                      onChange={(e) => {
                        onChange(e);
                        googleDataLayer.createUserClickInputEvent('date_of_birth');
                      }}
                      onBlur={onBlur}
                      onCalendarClose={() => props.trigger(guestDate)}
                      selected={value}
                      openToDate={maxInfantAge}
                      showYearDropdown
                      // adding 2 years to todays date
                      minDate={maxInfantAge}
                      maxDate={new Date(checkoutDate)}
                      className={"dob gb-mask"}
                      id={guestDate}
                      // customInput is used to apply custom attributes to the HTML element rendered by ReactDatePicker.
                      // In this scenario we are adding 'daterange' attribute for testing purposes as the ReactDatePicker cannot be rendered in Jest test.
                      // Also, we are checking if environment where code is running is Jest test or not to not generate this attribute in the other environment than testing.
                      customInput={
                        (process.env.JEST_WORKER_ID !== undefined) ? <input daterange-infant={`${new Date(checkoutDate)} - ${maxInfantAge}`} /> : <input inputmode="none" autocomplete="off" data-cy="dob"/>
                      }
                      {...(isCro1381 && {
                        popperPlacement: "bottom-start",
                        popperModifiers: [
                          {
                            name: "flip",
                            options: {
                              fallbackPlacements: ["bottom-start"],
                              allowedAutoPlacements: ["bottom-start"],
                            },
                          },
                        ],
                        showPopperArrow: false,
                      })}
                    />
                    {error && (
                      <StandardValidation
                      id={'dob'}
                      className={`input ${!isCro1381 ? 'dobValidation dobValidationSpacer' : ''}`}
                      valType="error"
                      icon="true"
                      >
                      {error.message}
                      </StandardValidation>
                    )}
                    {showSuccess && (
                      <StandardValidation
                        id={'dob'}
                        valType="success"
                        icon="true"
                        className={`input ${!isCro1381 ? 'dobValidation' : ''}`}
                      />
                    )}
                  </>
                  )
                }}
              />
            </div>
          </React.Fragment>
        );
      }

      // here;
      guestListFieldsGrouped.push(guestListFields);
    }
  });
  return (
    <div className="guest-list">
      {guestListFieldsGrouped.map((guestListField, index) => (
        <div className="guest" key={index}>
          {!isCro1381 ?
            <>{guestListField}</>
          :
            <>
              {guestListField[0]}
              <div className="cro1381HalfForm">
                {/* Here we're skipping the first element since it's assumed to be the header */}
                {guestListField.slice(1).map((field, fieldIndex) => (
                  <React.Fragment key={fieldIndex}>
                    {field}
                  </React.Fragment>
                ))}
              </div>
            </>
          }
        </div>
      ))}
    </div>
  );
};

export const TransportAttributeFields = ({ props, att, index, ticket, attributeType, attributeTypeAndIndex, getValues, isHotelCart }) => {
  const { t } = useTranslation("common");

  // const [isFiledTouched, setIsFieldTouched] = useState(false);
  // const [fieldValue, setFiledVale] = useState(false);

  let guestListFieldsGrouped = [];
  const guestFieldName = att.key + "_" + index + "_" + index;

  // CRO-1381-checkout-redesign
  const config = getDomainConfig();
  const abContext = useContext(ABContext);
  const [isCro1381, setIsCro1381] = useState(false);

  useEffect(() => {
    if ((config.brand !== 'Sw') && abContext.cro1381 !== undefined) setIsCro1381(abContext.cro1381.variant);
  }, [abContext]);
  // END CRO-1381-checkout-redesign

  // useEffect(() => {
  //   setFiledVale(getValues([guestFieldName]));
  // }, [isFiledTouched]);

  const guestListDateFields = [];

  // Switching off populating DOB fields, was causing an issue for Eurostar
  // const populateDateValues = (event, props, attributeTypeAndIndex) => {

  //   const inputFields = document.querySelectorAll(
  //     `[attributetypeandindex=${attributeTypeAndIndex}]`
  //   );
  //   inputFields.forEach((inputField) => {
  //     // get the key.
  //     const attributeName = inputField.getAttribute("id");
  //     props.setValue(attributeName, event, { shouldDirty:true, shouldTouch:true, shouldValidate: true });
  //   });

  // };

  if (attributeType === "date") {
    let minDate = new Date();
    let maxDate;

    if(isHotelCart) {
      const childrenAges = ticket.rawAttributes[24][0].split(',');
      const travelDate = new Date(ticket.rawAttributes[5].toLocaleString('en-GB'))

      if (attributeTypeAndIndex.includes('child')) {
        maxDate = new Date();
        maxDate = calculateDOBRange(travelDate, 2);
        minDate = calculateDOBRange(travelDate, 10);
      } else if(attributeTypeAndIndex.includes('adult') && (+childrenAges[index] >= 12 && +childrenAges[index] <= 17)) {
        maxDate = new Date();
        maxDate = calculateDOBRange(travelDate, 11);
        minDate= calculateDOBRange(travelDate, 16);
      } else {
        maxDate = new Date();
        minDate = '';
        maxDate = calculateDOBRange(new Date(), 17);
      }
    }

    guestListDateFields.push(
      <React.Fragment>
      <div className="date-item">
        <StandardLabel htmlFor={guestFieldName} required="true">
          {t(att.title)}
        </StandardLabel>
          <Controller
            control={props.control}
            name={guestFieldName}
            rules={{required: `${t('please-enter-value-for')} ${t(att.title)}`}}
            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error, invalid }}) => {
              const showSuccess = value && !invalid;

              return (
                <>
                  <ReactDatePicker
                  name={guestFieldName}
                  ref={(e) => {e && ref(e.input)}}
                  placeholderText={t(att.title)}
                  dateFormat="dd MMM yyyy"
                  onChange={(e) => {
                    onChange(e);
                    googleDataLayer.createUserClickInputEvent('date_of_birth');
                  }}
                  onBlur={onBlur}
                  onCalendarClose={() => props.trigger(guestFieldName)}
                  selected={value}
                  showYearDropdown
                  className={"dob gb-mask"}
                  id={guestFieldName}
                  minDate={minDate}
                  maxDate={maxDate}
                  {...(isCro1381 && {
                    popperPlacement: "bottom-start",
                    popperModifiers: [
                      {
                        name: "flip",
                        options: {
                          fallbackPlacements: ["bottom-start"],
                          allowedAutoPlacements: ["bottom-start"],
                        },
                      },
                    ],
                    showPopperArrow: false,
                  })}
                  customInput={
                    <input inputmode="none" autocomplete="off" data-cy="dob"/>
                  }
                />
                {error && (
                  <StandardValidation
                  id="dob"
                  valType="error"
                  icon="true"
                  className={`input ${!isCro1381 ? 'dobValidation dobValidationSpacer' : ''}`}
                  >
                  {error.message}
                  </StandardValidation>
                )}
                {showSuccess && (
                  <StandardValidation
                    id="dob"
                    valType="success"
                    icon="true"
                    className={`input ${!isCro1381 ? 'dobValidation' : ''}`}
                  />
                )}
              </>
            )
          }}
        />
      </div>
    </React.Fragment>
    );
  }
  guestListFieldsGrouped.push(guestListDateFields);
  return (
    <div className="guest-list">
      {guestListFieldsGrouped.map((guestListField, index) => (
        <div key={index}>{guestListField}</div>
      ))}
    </div>
  );
};

export const AttributeField = ({ props, att, index, ticket, setValue, maskingClass, dataLayerInput, fieldTitle, product, attTitle, isHotelCart, label, setGuestInfo}) => {

  const { t } = useTranslation('common');
  const languageCode = getLanguageCode('Atd');

  const getTranslatedAttributeTitle = (attributeTitle) => {
    if (languageCode === 'de') {
      return `${t(attributeTitle)}n an.`
    }
    return t(attributeTitle)
  }

  // Get the attribute id.
  const attribute_id = att.attribute_id.split("-")[2];
  // Default the returned component to a text field.
  let component = (
    <Textfield
      register={props.register}
      errors={props.errors}
      inputSize={32}
      required={att.required}
      placeholder={label !== undefined ? t(label) : t(att.title)}
      name={att.key + "_" + index}
      attributeTypeAndIndex={ticket.type.toLowerCase() + "_" + index}
      label={label !== undefined ? t(label) : t(att.title)}
      type={att.type}
      errorText={`${t('please-enter-value-for')} ${label !== undefined ? t(label) : getTranslatedAttributeTitle(att.title)}`}
      touched={props.touchedFields[att.key + "_" + index]}
      setValue={props.setValue}
      maskingClass={maskingClass}
      dataLayerInput={dataLayerInput}
      fieldTitle={fieldTitle}
      product={product}
      attTitle={attTitle}
      setGuestInfo={setGuestInfo}
    />
  );

  // Switch based on attribute type, override if not a textfield.
  switch (att.type) {
    case "select":
      const fieldOptions = att.options.map((opt) => {
        return (
          <option key={opt} value={opt}>
            {opt}
          </option>
        );
      });
      const fieldName =att.key + "_" + index
      const onValueChange = (e) => {
        props.setValue(props.name,e.target.value,{shouldDirty:true,shouldTouch:true})
      }
      component = (<Select
          register={props.register}
          showInitialOption={true}
          errors={props.errors}
          getValues={props.getValues}
          inputSize={32}
          required={att.required}
          placeholder={att.title}
          name={fieldName}
          options={fieldOptions}
          control={props.control}
          initialOption={t('please-select')}
          label={t(att.title)}
          errorText={`${t('please-enter-value-for')} ${t(att.title)}`}
          onChange={onValueChange}
          touched={props.touchedFields[fieldName]}
          setValue={props.setValue}
          dataLayerInput={() => googleDataLayer.createUserClickInputEvent(fieldName)}
        />
      )
      break;
    case "textarea":
      if (+attribute_id === 50) {
        // We have the fabled guest list attribute, time to do some magic.
        component = (
          <GuestListAttribute
            props={props}
            att={att}
            index={index}
            ticket={ticket}
            attributeTypeAndIndex={ticket.type.toLowerCase() + "_" + index}
            maskingClass={maskingClass}
            dataLayerInput={dataLayerInput}
          />
        );
      }
      break;
    case "date":
      if (ticket.tags.map((tag) => tag.includes("hotel-transport"))) {
        component = (
          <TransportAttributeFields
            props={props}
            att={att}
            index={index}
            ticket={ticket}
            attributeType = {att.type}
            attributeTypeAndIndex={"date_" + ticket.type.toLowerCase() + "_0_" + index}
            getValues={props.getValues}
            isHotelCart={isHotelCart}
            maskingClass={maskingClass}
            dataLayerInput={dataLayerInput}
          />
        );
      }
        break;
      default:
        return component
  }
  // Return the needed component.
  return component;
};
